import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Search, Filter, Eye, Ban, Check, Wallet, Building2,
  ChevronLeft, ChevronRight, RefreshCw, Download, Crown,
  Mail, Phone, Calendar, TrendingUp, CreditCard, Zap,
  CheckCircle, XCircle, Clock, ArrowUpDown, ArrowUp, ArrowDown,
  UserCheck, ExternalLink, MoreHorizontal, Shield, ChevronDown
} from 'lucide-react';
import { adminAPI } from '../../api';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { formatCredits } from '../../utils/credits';

// Plan badge colors
const PLAN_COLORS = {
  starter: 'bg-slate-100 text-slate-700 border-slate-200',
  growth: 'bg-blue-50 text-blue-700 border-blue-200',
  professional: 'bg-purple-50 text-purple-700 border-purple-200',
  enterprise: 'bg-amber-50 text-amber-700 border-amber-200'
};

const PLAN_LABELS = {
  starter: 'Starter',
  growth: 'Growth',
  professional: 'Professional',
  enterprise: 'Enterprise'
};

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, color, subtext }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
        {subtext && <p className="text-xs text-slate-400">{subtext}</p>}
      </div>
    </div>
  </div>
);

const AdminPartners = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    plan: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    disabled: 0,
    byPlan: {}
  });
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);

  // Debounced search for autocomplete
  const searchAutocomplete = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    try {
      setSearchLoading(true);
      const response = await adminAPI.getPartners({ search: term, limit: 8 });
      setSearchSuggestions(response.data.partners || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Autocomplete search failed:', error);
      setSearchSuggestions([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounce effect for autocomplete
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      searchAutocomplete(searchTerm);
    }, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, searchAutocomplete]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const searchContainer = document.querySelector('[data-search-container]');
      if (searchContainer && !searchContainer.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectSuggestion = (partner) => {
    setSearchTerm(partner.email);
    setShowSuggestions(false);
    // Navigate to partner detail
    navigate(`/admin/partners/${partner.partner_id}`);
  };

  const fetchPartners = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        status: filters.status || undefined,
        plan: filters.plan || undefined
      };
      
      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      const response = await adminAPI.getPartners(params);
      let fetchedPartners = response.data.partners || [];
      
      // Calculate stats from ALL partners (do this before pagination in real app)
      // For now, use what we have
      const activeCount = fetchedPartners.filter(p => p.is_active !== false).length;
      const disabledCount = fetchedPartners.filter(p => p.is_active === false).length;
      const planCounts = {};
      fetchedPartners.forEach(p => {
        const plan = p.subscription_plan || 'starter';
        planCounts[plan] = (planCounts[plan] || 0) + 1;
      });
      
      // Client-side sorting
      fetchedPartners = [...fetchedPartners].sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (sortConfig.key === 'wallet_balance' || sortConfig.key === 'total_credits_used') {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        } else if (sortConfig.key === 'created_at') {
          aVal = new Date(aVal || 0).getTime();
          bVal = new Date(bVal || 0).getTime();
        } else {
          aVal = String(aVal || '').toLowerCase();
          bVal = String(bVal || '').toLowerCase();
        }
        
        if (sortConfig.direction === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });
      
      setPartners(fetchedPartners);
      setPagination(prev => ({
        ...prev,
        page: response.data.page,
        total: response.data.total,
        totalPages: response.data.total_pages
      }));
      setStats({
        total: response.data.total,
        active: activeCount,
        disabled: disabledCount,
        byPlan: planCounts
      });
    } catch (error) {
      toast.error('Failed to load partners');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, pagination.limit, sortConfig]);

  useEffect(() => {
    fetchPartners(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchPartners(1);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Re-sort when sortConfig changes
  useEffect(() => {
    if (partners.length > 0) {
      const sorted = [...partners].sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (sortConfig.key === 'wallet_balance' || sortConfig.key === 'total_credits_used') {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        } else if (sortConfig.key === 'created_at') {
          aVal = new Date(aVal || 0).getTime();
          bVal = new Date(bVal || 0).getTime();
        } else {
          aVal = String(aVal || '').toLowerCase();
          bVal = String(bVal || '').toLowerCase();
        }
        
        if (sortConfig.direction === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });
      setPartners(sorted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortConfig]);

  const handleToggleStatus = async (partner) => {
    try {
      await adminAPI.togglePartnerStatus(partner.partner_id);
      toast.success(`Partner ${partner.is_active ? 'disabled' : 'enabled'} successfully`);
      fetchPartners(pagination.page);
    } catch (error) {
      toast.error('Failed to update partner status');
    }
    setActionMenuOpen(null);
  };

  const handleViewAsPartner = async (partner) => {
    try {
      const response = await adminAPI.impersonatePartner(partner.partner_id);
      // Store token and navigate to partner dashboard
      localStorage.setItem('impersonate_token', response.data.token);
      localStorage.setItem('impersonate_partner', JSON.stringify(response.data.partner));
      toast.success(`Viewing as ${partner.business_name || partner.email}`);
      // Open in new tab
      window.open('/partners/dashboard', '_blank');
    } catch (error) {
      toast.error('Failed to view as partner');
    }
    setActionMenuOpen(null);
  };

  const exportPartners = () => {
    // Simple CSV export
    const headers = ['Business Name', 'Email', 'Plan', 'Balance', 'Total Used', 'Status', 'Joined'];
    const rows = partners.map(p => [
      p.business_name || '',
      p.email || '',
      p.subscription_plan || 'starter',
      p.wallet_balance || 0,
      p.total_credits_used || 0,
      p.is_active !== false ? 'Active' : 'Disabled',
      p.created_at ? format(parseISO(p.created_at), 'yyyy-MM-dd') : ''
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `partners_export_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    toast.success('Partners exported successfully');
  };

  const renderSortableHeader = (column, label, className = '') => {
    const isActive = sortConfig.key === column;
    return (
      <th 
        className={`text-left py-4 px-4 text-slate-500 font-medium cursor-pointer hover:bg-slate-50 select-none ${className}`}
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-1">
          {label}
          {isActive ? (
            sortConfig.direction === 'asc' ? 
              <ArrowUp className="w-3 h-3 text-blue-600" /> : 
              <ArrowDown className="w-3 h-3 text-blue-600" />
          ) : (
            <ArrowUpDown className="w-3 h-3 text-slate-300" />
          )}
        </div>
      </th>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6 overflow-x-hidden" data-testid="admin-partners-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />
            <span className="truncate">Partner Management</span>
          </h1>
          <p className="text-sm text-slate-500">View and manage all partner accounts</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportPartners}
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-1.5 text-xs sm:text-sm"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={() => fetchPartners(pagination.page)}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5 text-xs sm:text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard 
          icon={Users} 
          label="Total Partners" 
          value={stats.total} 
          color="bg-blue-100 text-blue-600"
        />
        <StatsCard 
          icon={CheckCircle} 
          label="Active" 
          value={stats.active} 
          color="bg-emerald-100 text-emerald-600"
        />
        <StatsCard 
          icon={XCircle} 
          label="Disabled" 
          value={stats.disabled} 
          color="bg-red-100 text-red-600"
        />
        <StatsCard 
          icon={Crown} 
          label="Paid Plans" 
          value={(stats.byPlan.growth || 0) + (stats.byPlan.professional || 0) + (stats.byPlan.enterprise || 0)} 
          color="bg-amber-100 text-amber-600"
          subtext={`${stats.byPlan.starter || 0} on Starter`}
        />
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search with Autocomplete */}
          <div className="flex-1 relative" data-search-container>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
            {searchLoading && (
              <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
            )}
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
              className="w-full pl-10 pr-10 py-2 border border-slate-200 rounded-lg text-sm"
              placeholder="Search by name, email, or phone..."
              data-testid="partner-search-input"
            />
            
            {/* Autocomplete Dropdown */}
            <AnimatePresence>
              {showSuggestions && searchSuggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto"
                  data-testid="search-suggestions"
                >
                  {searchSuggestions.map((partner) => (
                    <button
                      key={partner.partner_id}
                      onClick={() => handleSelectSuggestion(partner)}
                      className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-3 border-b border-slate-100 last:border-0 transition-colors"
                      data-testid={`suggestion-${partner.partner_id}`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        partner.is_admin ? 'bg-purple-100' : 'bg-blue-100'
                      }`}>
                        {partner.is_admin ? (
                          <Shield className="w-5 h-5 text-purple-600" />
                        ) : (
                          <Building2 className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          {partner.business_name || 'No Business Name'}
                        </p>
                        <p className="text-sm text-slate-500 truncate flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          {partner.email}
                        </p>
                        {partner.phone && (
                          <p className="text-xs text-slate-400 truncate flex items-center gap-2">
                            <Phone className="w-3 h-3" />
                            {partner.phone}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${
                          PLAN_COLORS[partner.subscription_plan] || PLAN_COLORS.starter
                        }`}>
                          {PLAN_LABELS[partner.subscription_plan] || 'Starter'}
                        </span>
                        <span className="text-xs text-slate-500">
                          {formatCredits(partner.wallet_balance || 0)} cr
                        </span>
                      </div>
                    </button>
                  ))}
                  <div className="px-4 py-2 bg-slate-50 text-xs text-slate-500 text-center border-t">
                    Press Enter to search all • Click to view partner
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* No results message */}
            <AnimatePresence>
              {showSuggestions && searchTerm.length >= 2 && searchSuggestions.length === 0 && !searchLoading && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 p-4 text-center"
                >
                  <p className="text-slate-500 text-sm">No partners found matching &quot;{searchTerm}&quot;</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm bg-white min-w-[140px]"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
          </select>
          
          {/* Plan Filter */}
          <select
            value={filters.plan}
            onChange={(e) => setFilters(prev => ({ ...prev, plan: e.target.value }))}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm bg-white min-w-[160px]"
          >
            <option value="">All Plans</option>
            <option value="starter">Starter</option>
            <option value="growth">Growth</option>
            <option value="professional">Professional</option>
            <option value="enterprise">Enterprise</option>
          </select>
          
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            Apply
          </button>
        </div>
      </div>

      {/* Partners Table/Cards */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : partners.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">No partners found</p>
            <p className="text-sm">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {renderSortableHeader('business_name', 'Partner')}
                    {renderSortableHeader('subscription_plan', 'Plan')}
                    {renderSortableHeader('wallet_balance', 'Balance')}
                    {renderSortableHeader('total_credits_used', 'Used')}
                    <th className="text-left py-4 px-4 text-slate-500 font-medium">Status</th>
                    {renderSortableHeader('created_at', 'Joined')}
                    <th className="text-right py-4 px-4 text-slate-500 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {partners.map((partner) => (
                    <tr key={partner.partner_id} className="hover:bg-slate-50 transition-colors">
                      {/* Partner Info */}
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            partner.is_admin ? 'bg-purple-100' : 'bg-blue-100'
                          }`}>
                            {partner.is_admin ? (
                              <Shield className="w-5 h-5 text-purple-600" />
                            ) : (
                              <Building2 className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 flex items-center gap-1">
                              {partner.business_name || 'No Business Name'}
                              {partner.is_admin && (
                                <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">Admin</span>
                              )}
                            </p>
                            <p className="text-sm text-slate-500">{partner.email}</p>
                            {partner.phone && (
                              <p className="text-xs text-slate-400">{partner.phone}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      
                      {/* Plan */}
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                          PLAN_COLORS[partner.subscription_plan] || PLAN_COLORS.starter
                        }`}>
                          {partner.subscription_plan === 'enterprise' && <Crown className="w-3 h-3" />}
                          {PLAN_LABELS[partner.subscription_plan] || 'Starter'}
                        </span>
                      </td>
                      
                      {/* Balance */}
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1.5">
                          <Wallet className="w-4 h-4 text-emerald-500" />
                          <span className="font-medium text-slate-900">
                            {formatCredits(partner.wallet_balance)}
                          </span>
                        </div>
                      </td>
                      
                      {/* Used */}
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1.5">
                          <Zap className="w-4 h-4 text-amber-500" />
                          <span className="text-slate-600">
                            {formatCredits(partner.total_credits_used)}
                          </span>
                        </div>
                      </td>
                      
                      {/* Status */}
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                          partner.is_active !== false
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                          {partner.is_active !== false ? (
                            <>
                              <CheckCircle className="w-3 h-3" />
                              Active
                            </>
                          ) : (
                            <>
                              <XCircle className="w-3 h-3" />
                              Disabled
                            </>
                          )}
                        </span>
                      </td>
                    
                    {/* Joined */}
                    <td className="py-4 px-4 text-sm text-slate-500">
                      {partner.created_at ? format(parseISO(partner.created_at), 'MMM d, yyyy') : '-'}
                    </td>
                    
                    {/* Actions */}
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-1 relative">
                        <Link
                          to={`/admin/partners/${partner.partner_id}`}
                          className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        
                        {/* More Actions Menu */}
                        <div className="relative">
                          <button
                            onClick={() => setActionMenuOpen(actionMenuOpen === partner.partner_id ? null : partner.partner_id)}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          
                          <AnimatePresence>
                            {actionMenuOpen === partner.partner_id && (
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 z-20"
                              >
                                <button
                                  onClick={() => handleViewAsPartner(partner)}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  View as Partner
                                </button>
                                <Link
                                  to={`/admin/partners/${partner.partner_id}`}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                                  onClick={() => setActionMenuOpen(null)}
                                >
                                  <Eye className="w-4 h-4" />
                                  View Details
                                </Link>
                                <button
                                  onClick={() => handleToggleStatus(partner)}
                                  className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 ${
                                    partner.is_active !== false ? 'text-red-600' : 'text-emerald-600'
                                  }`}
                                >
                                  {partner.is_active !== false ? (
                                    <>
                                      <Ban className="w-4 h-4" />
                                      Disable Account
                                    </>
                                  ) : (
                                    <>
                                      <Check className="w-4 h-4" />
                                      Enable Account
                                    </>
                                  )}
                                </button>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-slate-100">
              {partners.map((partner) => (
                <div key={partner.partner_id} className="p-4 hover:bg-slate-50">
                  {/* Partner Header */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        partner.is_admin ? 'bg-purple-100' : 'bg-blue-100'
                      }`}>
                        {partner.is_admin ? (
                          <Shield className="w-5 h-5 text-purple-600" />
                        ) : (
                          <Building2 className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 text-sm truncate">
                          {partner.business_name || 'No Business Name'}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{partner.email}</p>
                      </div>
                    </div>
                    <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      partner.is_active !== false
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-red-50 text-red-700'
                    }`}>
                      {partner.is_active !== false ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  
                  {/* Partner Stats */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-xs text-slate-500">Plan</p>
                      <p className="text-xs font-medium text-slate-900">{PLAN_LABELS[partner.subscription_plan] || 'Starter'}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-xs text-slate-500">Balance</p>
                      <p className="text-xs font-medium text-emerald-600">{formatCredits(partner.wallet_balance)}</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2 text-center">
                      <p className="text-xs text-slate-500">Used</p>
                      <p className="text-xs font-medium text-amber-600">{formatCredits(partner.total_credits_used)}</p>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2">
                    <Link
                      to={`/admin/partners/${partner.partner_id}`}
                      className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium text-center"
                    >
                      View Details
                    </Link>
                    <button
                      onClick={() => handleViewAsPartner(partner)}
                      className="px-3 py-2 border border-slate-200 rounded-lg text-xs"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        
        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="px-4 py-3 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs sm:text-sm text-slate-500">
              Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchPartners(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-xs sm:text-sm"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Previous</span>
              </button>
              <span className="px-3 py-1.5 text-xs sm:text-sm text-slate-600">
                {pagination.page} / {pagination.totalPages}
              </span>
              <button
                onClick={() => fetchPartners(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-xs sm:text-sm"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close menu */}
      {actionMenuOpen && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => setActionMenuOpen(null)}
        />
      )}
    </div>
  );
};

export default AdminPartners;
